"use client";

import { ChevronsLeft, ChevronsRight } from "lucide-react";
import Link from "next/link";

import AccountMenu from "@/components/nav/AccountMenu";
import RailTooltip from "@/components/nav/RailTooltip";
import { IconMapping } from "@/components/Sidebar/IconMapping";
import {
  NAV_CURRENT_PILL,
  NAV_RAIL_WIDTH,
  NAV_REST_ITEM,
} from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

const RAIL_BUTTON =
  "flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 text-[10px] font-medium leading-tight transition-colors";

// Coral marks the menu the page belongs to; stone marks another menu whose
// panel is open; the rest take the hover tint only.
function railButtonClass({ current = false, open = false } = {}) {
  return cn(
    RAIL_BUTTON,
    current
      ? NAV_CURRENT_PILL
      : open
        ? "bg-stone-200/50 text-gray-900"
        : NAV_REST_ITEM,
  );
}

/**
 * The icon rail: one button per menu, Home above them, and the panel toggle
 * and the account menu at the foot. The studio's mark and search sit above it
 * (StudioBand, NavSearchRow). Every control names itself on hover (RailTooltip).
 * `currentKey` is the menu the page belongs to (`aria-current`); `openKey` is
 * the menu the panel is showing, if open (`aria-expanded`).
 */
export default function NavRail({
  menus,
  currentKey,
  openKey,
  onSelectMenu,
  isHome,
  user,
  panelOpen,
  panelAvailable,
  onTogglePanel,
  menuButtonRef,
  tipMenuKey,
}) {
  const Home = IconMapping.IoHomeOutline;
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col items-center gap-1 px-2 pb-3 pt-2",
        NAV_RAIL_WIDTH,
      )}
    >
      <RailTooltip label="Home">
        <Link
          href="/"
          aria-current={isHome ? "page" : undefined}
          className={railButtonClass({ current: isHome })}
        >
          <Home className="h-5 w-5" />
          Home
        </Link>
      </RailTooltip>

      {menus.map((menu) => {
        const Icon = IconMapping[menu.iconName];
        return (
          <RailTooltip key={menu.key} label={menu.menuLabel}>
            <button
              type="button"
              ref={menu.key === tipMenuKey ? menuButtonRef : undefined}
              aria-current={menu.key === currentKey ? "true" : undefined}
              aria-expanded={menu.key === openKey}
              onClick={() => onSelectMenu(menu.key)}
              className={railButtonClass({
                current: menu.key === currentKey,
                open: menu.key === openKey,
              })}
            >
              {Icon && <Icon className="h-5 w-5" />}
              <span className="line-clamp-2 max-w-full break-words text-center">
                {menu.menuLabel}
              </span>
            </button>
          </RailTooltip>
        );
      })}

      <div className="mt-auto flex flex-col items-center gap-2 pt-2">
        {panelAvailable && (
          <RailTooltip label={panelOpen ? "Hide panel" : "Show panel"}>
            <button
              type="button"
              aria-pressed={panelOpen}
              aria-label={panelOpen ? "Hide panel" : "Show panel"}
              onClick={onTogglePanel}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-stone-200/50 hover:text-gray-700"
            >
              {panelOpen ? (
                <ChevronsLeft className="h-4 w-4" />
              ) : (
                <ChevronsRight className="h-4 w-4" />
              )}
            </button>
          </RailTooltip>
        )}
        <AccountMenu user={user} />
      </div>
    </div>
  );
}
